// Google Maps Service - Handles Google Maps API integration
import config from '../config';

let googleMapsLoaded = false;
let googleMapsPromise = null;

/**
 * Load Google Maps JavaScript API
 * @returns {Promise} Resolves when Google Maps is loaded
 */
export const loadGoogleMapsAPI = () => {
  const GOOGLE_API_KEY = config.mapsApiKey;

  // Log API key status (masked for security)
  if (GOOGLE_API_KEY) {
    const maskedKey = GOOGLE_API_KEY.substring(0, 10) + '...' + GOOGLE_API_KEY.substring(GOOGLE_API_KEY.length - 4);
    console.log('🔑 API Key loaded from config:', maskedKey);
    console.log('🔑 API Key length:', GOOGLE_API_KEY.length);
    console.log('🔑 API Key starts with:', GOOGLE_API_KEY.substring(0, 4));
  } else {
    console.error('❌ Google Maps API key not configured');
    return Promise.reject(new Error('Google Maps API key not configured'));
  }

  // Return existing promise if already loading
  if (googleMapsPromise) {
    console.log('📍 Google Maps API already loading, returning existing promise');
    return googleMapsPromise;
  }

  // Return immediately if already loaded
  if (googleMapsLoaded && window.google && window.google.maps) {
    console.log('✅ Google Maps API already loaded');
    // Verify Places library is available
    if (!window.google.maps.places) {
      console.warn('⚠️ Google Maps loaded but Places library not available');
      console.warn('⚠️ Make sure you include &libraries=places in the script URL');
    } else {
      console.log('✅ Places library is available');
    }
    return Promise.resolve(window.google.maps);
  }

  // Check if script already exists
  const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
  if (existingScript) {
    console.log('📍 Google Maps script exists, waiting for load...');
    googleMapsPromise = new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds max wait
      
      const checkLoaded = setInterval(() => {
        attempts++;
        if (window.google && window.google.maps && window.google.maps.places) {
          clearInterval(checkLoaded);
          googleMapsLoaded = true;
          console.log('✅ Google Maps API loaded from existing script');
          resolve(window.google.maps);
        } else if (attempts >= maxAttempts) {
          clearInterval(checkLoaded);
          googleMapsPromise = null;
          reject(new Error('Timeout waiting for Google Maps API to load'));
        }
      }, 100);
    });
    return googleMapsPromise;
  }

  // Load the script
  console.log('📍 Loading Google Maps API script...');
  
  // Check if we're in a native WebView
  const isNative = typeof window !== 'undefined' && 
    (window.location.protocol === 'capacitor:' || 
     window.location.protocol === 'capacitor-http:' || 
     window.location.protocol === 'capacitor-https:' ||
     /Android.*wv/.test(navigator.userAgent));
  
  if (isNative) {
    console.log('📍 Detected native WebView environment');
  }
  
  googleMapsPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    const scriptUrl = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places`;
    script.src = scriptUrl;
    
    // Log the URL (with masked key for security)
    const maskedUrl = scriptUrl.replace(GOOGLE_API_KEY, GOOGLE_API_KEY.substring(0, 10) + '...');
    console.log('📍 Script URL:', maskedUrl);
    console.log('📍 Full script URL length:', scriptUrl.length);
    console.log('📍 Environment:', isNative ? 'Native WebView' : 'Web Browser');
    
    script.async = true;
    script.defer = true;

    // Add timeout
    const timeout = setTimeout(() => {
      googleMapsPromise = null;
      reject(new Error('Timeout loading Google Maps API'));
    }, 15000); // 15 second timeout

    script.onload = () => {
      clearTimeout(timeout);
      console.log('✅ Google Maps script loaded successfully');
      console.log('📍 Checking if window.google.maps is available...');
      
      // Wait a bit for the API to be fully initialized
      setTimeout(() => {
        console.log('📍 window.google exists:', !!window.google);
        console.log('📍 window.google.maps exists:', !!(window.google && window.google.maps));
        console.log('📍 window.google.maps.places exists:', !!(window.google && window.google.maps && window.google.maps.places));
        
        if (window.google && window.google.maps) {
          if (window.google.maps.places) {
            googleMapsLoaded = true;
            console.log('✅ Google Maps API loaded successfully');
            console.log('✅ Places library is available');
            console.log('📍 AutocompleteService available:', !!window.google.maps.places.AutocompleteService);
            resolve(window.google.maps);
          } else {
            googleMapsPromise = null;
            console.error('❌ Google Maps API loaded but Places library not available');
            console.error('❌ This means Places API is NOT enabled in your Google Cloud project');
            console.error('❌ Steps to fix:');
            console.error('   1. Go to Google Cloud Console');
            console.error('   2. Navigate to: APIs & Services > Library');
            console.error('   3. Search for "Places API"');
            console.error('   4. Click "Enable" if it shows "Enable API"');
            console.error('   5. Wait 1-2 minutes for changes to propagate');
            reject(new Error('Google Maps API loaded but Places library not available. Please enable Places API in Google Cloud Console: APIs & Services > Library > Places API > Enable'));
          }
        } else {
          googleMapsPromise = null;
          console.error('❌ Google Maps script loaded but window.google.maps is not available');
          console.error('❌ This might indicate a script loading issue or API key problem');
          reject(new Error('Google Maps API script loaded but window.google.maps is not available'));
        }
      }, 500); // Increased wait time to 500ms
    };

    script.onerror = (error) => {
      clearTimeout(timeout);
      googleMapsPromise = null;
      console.error('❌ Failed to load Google Maps script');
      console.error('❌ Error details:', error);
      console.error('❌ This might be due to:');
      console.error('   1. Network security config blocking the request');
      console.error('   2. API key restrictions blocking native app requests');
      console.error('   3. CORS issues in WebView');
      console.error('   4. Check Android network_security_config.xml allows maps.googleapis.com');
      if (isNative) {
        console.error('   5. For native apps, check API key has Android app restrictions configured');
      }
      reject(new Error('Failed to load Google Maps. Check network security config and API key restrictions.'));
    };

    document.head.appendChild(script);
  });

  return googleMapsPromise;
};

/**
 * Search for address predictions using Google Places Autocomplete
 * @param {string} query - Search query
 * @returns {Promise<Array>} Array of predictions
 */
export const searchAddressPredictions = async (query) => {
  try {
    console.log('📍 Loading Google Maps API...');
    await loadGoogleMapsAPI();
    console.log('✅ Google Maps API loaded');

    if (!window.google || !window.google.maps) {
      throw new Error('Google Maps API not available');
    }

    if (!window.google.maps.places) {
      throw new Error('Google Maps Places library not loaded. Make sure the Places API is enabled for your API key.');
    }

    console.log('📍 Creating AutocompleteService...');
    
    return new Promise((resolve, reject) => {
      // Add timeout to prevent hanging
      const timeout = setTimeout(() => {
        console.error('❌ Address search request timed out after 10 seconds');
        console.error('📍 This usually means the Places API callback was never called.');
        console.error('📍 Possible causes:');
        console.error('   1. Places API not enabled for your API key');
        console.error('   2. API key has restrictions that block the request');
        console.error('   3. Billing/quota issues with Google Cloud');
        console.error('   4. Network/CORS issues');
        reject(new Error('Address search request timed out. Please check if Places API is enabled for your Google Maps API key.'));
      }, 10000); // 10 second timeout

      try {
        if (!window.google.maps.places.AutocompleteService) {
          clearTimeout(timeout);
          throw new Error('AutocompleteService is not available. Places library may not be loaded correctly.');
        }

        const service = new window.google.maps.places.AutocompleteService();
        console.log('📍 AutocompleteService created, calling getPlacePredictions...');
        console.log('📍 Search query:', query);

        service.getPlacePredictions(
          {
            input: query,
            componentRestrictions: { country: 'in' }, // Restrict to India
          },
          (predictions, status) => {
            clearTimeout(timeout);
            
            console.log('📍 Places API callback received:', { 
              status, 
              statusName: window.google?.maps?.places?.PlacesServiceStatus?.[status] || status,
              predictionsCount: predictions?.length || 0 
            });
            
            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
              console.log('✅ Address predictions received successfully');
              resolve(predictions);
            } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
              console.log('📍 No results found for query:', query);
              resolve([]);
            } else {
              // Log all possible status codes for debugging
              const statusNames = {
                [window.google.maps.places.PlacesServiceStatus.OK]: 'OK',
                [window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS]: 'ZERO_RESULTS',
                [window.google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT]: 'OVER_QUERY_LIMIT',
                [window.google.maps.places.PlacesServiceStatus.REQUEST_DENIED]: 'REQUEST_DENIED',
                [window.google.maps.places.PlacesServiceStatus.INVALID_REQUEST]: 'INVALID_REQUEST',
                [window.google.maps.places.PlacesServiceStatus.UNKNOWN_ERROR]: 'UNKNOWN_ERROR',
              };
              
              const statusName = statusNames[status] || `UNKNOWN_STATUS_${status}`;
              const errorMessage = `Places API error: ${statusName} (${status})`;
              console.error('❌ Places API error:', errorMessage);
              
              if (status === window.google.maps.places.PlacesServiceStatus.REQUEST_DENIED) {
                reject(new Error('Places API request denied. Please check if Places API is enabled and your API key has the correct permissions.'));
              } else if (status === window.google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT) {
                reject(new Error('Places API quota exceeded. Please check your Google Cloud billing.'));
              } else {
                reject(new Error(errorMessage));
              }
            }
          }
        );
        
        console.log('📍 getPlacePredictions called, waiting for callback...');
      } catch (error) {
        clearTimeout(timeout);
        console.error('❌ Error creating or calling AutocompleteService:', error);
        reject(error);
      }
    });
  } catch (error) {
    console.error('❌ Error searching addresses:', error);
    throw error;
  }
};

/**
 * Get place details and coordinates from place_id
 * @param {string} placeId - Google Place ID
 * @returns {Promise<Object>} Place details with coordinates
 */
export const getPlaceDetails = async (placeId) => {
  try {
    await loadGoogleMapsAPI();

    return new Promise((resolve, reject) => {
      const service = new window.google.maps.places.PlacesService(
        document.createElement('div')
      );

      service.getDetails(
        {
          placeId: placeId,
          fields: ['geometry', 'address_components', 'formatted_address'],
        },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();

            // Extract city, state, and pincode from address components
            let city = '';
            let state = '';
            let pincode = '';
            const addressComponents = place.address_components || [];

            for (const component of addressComponents) {
              // Extract city (locality or administrative_area_level_2)
              if (!city && component.types.includes('locality')) {
                city = component.long_name;
              } else if (!city && component.types.includes('administrative_area_level_2')) {
                city = component.long_name;
              }
              
              // Extract state (administrative_area_level_1)
              if (!state && component.types.includes('administrative_area_level_1')) {
                state = component.long_name;
              }
              
              // Extract pincode (postal_code)
              if (!pincode && component.types.includes('postal_code')) {
                pincode = component.long_name;
              }
            }

            resolve({
              latitude: parseFloat(lat.toFixed(6)),
              longitude: parseFloat(lng.toFixed(6)),
              city: city || null,
              state: state || null,
              pincode: pincode || null,
              address: place.formatted_address,
            });
          } else {
            reject(new Error(`Places service error: ${status}`));
          }
        }
      );
    });
  } catch (error) {
    console.error('Error getting place details:', error);
    throw error;
  }
};

/**
 * Reverse geocode coordinates to get address details
 * @param {number} latitude - Latitude coordinate
 * @param {number} longitude - Longitude coordinate
 * @returns {Promise<Object>} Address details with city, state, pincode, and formatted address
 */
export const reverseGeocode = async (latitude, longitude) => {
  try {
    await loadGoogleMapsAPI();

    return new Promise((resolve, reject) => {
      const geocoder = new window.google.maps.Geocoder();
      const latlng = { lat: latitude, lng: longitude };

      geocoder.geocode({ location: latlng }, (results, status) => {
        if (status === window.google.maps.GeocoderStatus.OK && results && results.length > 0) {
          const result = results[0];
          
          // Extract city, state, and pincode from address components
          let city = '';
          let state = '';
          let pincode = '';
          const addressComponents = result.address_components || [];

          for (const component of addressComponents) {
            // Extract city (locality or administrative_area_level_2)
            if (!city && component.types.includes('locality')) {
              city = component.long_name;
            } else if (!city && component.types.includes('administrative_area_level_2')) {
              city = component.long_name;
            }
            
            // Extract state (administrative_area_level_1)
            if (!state && component.types.includes('administrative_area_level_1')) {
              state = component.long_name;
            }
            
            // Extract pincode (postal_code)
            if (!pincode && component.types.includes('postal_code')) {
              pincode = component.long_name;
            }
          }

          resolve({
            latitude: parseFloat(latitude.toFixed(6)),
            longitude: parseFloat(longitude.toFixed(6)),
            city: city || null,
            state: state || null,
            pincode: pincode || null,
            address: result.formatted_address,
          });
        } else {
          reject(new Error(`Geocoding failed: ${status}`));
        }
      });
    });
  } catch (error) {
    console.error('Error reverse geocoding:', error);
    throw error;
  }
};

/**
 * Fallback reverse geocode using OpenStreetMap Nominatim (no API key, works in WebView/native).
 * Use when Google reverseGeocode fails (e.g. in Capacitor WebView).
 * @param {number} latitude - Latitude coordinate
 * @param {number} longitude - Longitude coordinate
 * @returns {Promise<Object>} Same shape as reverseGeocode: { latitude, longitude, address, city, state, pincode }
 */
export const reverseGeocodeFallback = async (latitude, longitude) => {
  const url = `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`;
  const response = await fetch(url, {
    headers: { Accept: 'application/json', 'User-Agent': 'BookMyOP-App/1.0' }
  });
  if (!response.ok) throw new Error(`Nominatim error: ${response.status}`);
  const data = await response.json();
  const addr = data.address || {};
  const city = addr.city || addr.town || addr.village || addr.county || null;
  const state = addr.state || null;
  const pincode = addr.postcode || null;
  const address = data.display_name || [city, state, data.country].filter(Boolean).join(', ') || null;
  return {
    latitude: parseFloat(Number(latitude).toFixed(6)),
    longitude: parseFloat(Number(longitude).toFixed(6)),
    address,
    city,
    state,
    pincode
  };
};